<script lang="ts">
  import { onDestroy } from 'svelte';
  import { Chart, registerables } from 'chart.js';

  import { unitPreference } from '../../stores/preferences';
  import { consumptionLiters } from '../../utils/consumption';

  Chart.register(...registerables);

  let { 
    data = [], 
    color = "#3b82f6",
    resolution = "hourly",
    showCost = false,
    showForecast = false,
    showBaseline = true,
    forecastMode = "standalone",
    flowSummary = null,
    baseline = null,
    customUnit = undefined,
    customMultiplier = 1.0
  } = $props<{
    data?: any[];
    color?: string;
    resolution?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    showCost?: boolean;
    showForecast?: boolean;
    showBaseline?: boolean;
    forecastMode?: 'standalone' | 'stacked';
    flowSummary?: any;
    baseline?: any;
    customUnit?: string;
    customMultiplier?: number;
  }>();

  let canvasEl: HTMLCanvasElement;
  let chart: Chart | null = null;

  function formatLabel(d: any, res: string): string {
    if (!d?.bucket) return '';
    const dt = new Date(d.bucket);
    if (isNaN(dt.getTime())) return '';

    switch (res) {
      case 'hourly':
        return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      case 'daily':
        return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
      case 'weekly':
        return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
      case 'monthly':
        return dt.toLocaleDateString([], { month: 'short', year: '2-digit' });
      case 'yearly':
        return dt.toLocaleDateString([], { year: 'numeric' });
      default:
        return dt.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit' });
    }
  }

  function resolveLiters(d: any): number | null {
    if (!d) return null;
    let liters = consumptionLiters(d);
    if (liters !== null) return liters;
    const candidate = d.consumption_liters ?? d.daily_consumption ?? d.hourly_consumption ?? d.water_consumption;
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0) {
      return candidate;
    }
    return null;
  }

  function buildChart() {
    if (chart) chart.destroy();
    if (!canvasEl || data.length === 0) return;

    const labels = data.map((d: any) => formatLabel(d, resolution));
    const isImperial = $unitPreference === 'imperial';
    const unit = customUnit || (isImperial ? 'gal' : 'L');

    const values = data.map((d: any) => {
      const liters = resolveLiters(d);
      if (liters === null) return null;
      if (customUnit) {
        return liters * customMultiplier;
      }
      return liters * (isImperial ? 0.264172 : 1);
    });

    const datasets: any[] = [];
    const now = new Date();

    // Check for in-progress period
    let inProgressIndex = -1;
    let inProgressElapsedFraction = 1.0;
    let projectedTotalLiters: number | null = null;

    if (showForecast && data.length > 0) {
      const lastIdx = data.length - 1;
      const lastItem = data[lastIdx];
      const dt = lastItem?.bucket ? new Date(lastItem.bucket) : null;

      if (dt && !isNaN(dt.getTime())) {
        const diffMs = now.getTime() - dt.getTime();
        const diffHours = diffMs / (1000 * 3600);
        const diffDays = diffMs / (1000 * 86400);

        let isCurrent = false;
        let elapsed = 1.0;

        if (resolution === 'hourly') {
          // Active within the current 24-hour cycle
          isCurrent = diffHours >= 0 && diffHours < 24.0;
          elapsed = Math.max(0.08, now.getUTCMinutes() / 60);
          const actualLiters = resolveLiters(lastItem) ?? 0;
          projectedTotalLiters = actualLiters > 0 ? (actualLiters / elapsed) : null;
        } else if (resolution === 'daily') {
          // Active within the current 48-hour cycle
          isCurrent = diffDays >= 0 && diffDays < 2.0;
          elapsed = Math.max(0.08, (now.getUTCHours() * 60 + now.getUTCMinutes()) / 1440);
          const actualLiters = resolveLiters(lastItem) ?? 0;
          projectedTotalLiters = flowSummary?.projected_today_liters ?? (actualLiters > 0 ? actualLiters / elapsed : null);
        } else if (resolution === 'weekly') {
          // Active within the current 7-day cycle
          isCurrent = diffDays >= 0 && diffDays < 7.0;
          elapsed = Math.max(0.1, diffDays / 7.0);
          const actualLiters = resolveLiters(lastItem) ?? 0;
          projectedTotalLiters = flowSummary?.projected_week_liters ?? (actualLiters > 0 ? actualLiters / elapsed : null);
        } else if (resolution === 'monthly') {
          // Active within the current calendar month
          isCurrent = diffDays >= 0 && diffDays < 31.0;
          const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
          elapsed = Math.max(0.05, now.getUTCDate() / daysInMonth);
          const actualLiters = resolveLiters(lastItem) ?? 0;
          projectedTotalLiters = flowSummary?.projected_month_liters ?? (actualLiters > 0 ? actualLiters / elapsed : null);
        } else if (resolution === 'yearly') {
          // Active within the current calendar year
          isCurrent = diffDays >= 0 && diffDays < 366.0;
          const startOfYear = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
          const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / 86400000);
          const daysInYear = ((now.getUTCFullYear() % 4 === 0 && now.getUTCFullYear() % 100 !== 0) || now.getUTCFullYear() % 400 === 0) ? 366 : 365;
          elapsed = Math.max(0.01, dayOfYear / daysInYear);
          const actualLiters = resolveLiters(lastItem) ?? 0;
          projectedTotalLiters = flowSummary?.projected_year_liters ?? (actualLiters > 0 ? actualLiters / elapsed : null);
        }

        if (isCurrent && projectedTotalLiters !== null && projectedTotalLiters > 0) {
          inProgressIndex = lastIdx;
          inProgressElapsedFraction = elapsed;
        }
      }
    }

    if (showForecast && inProgressIndex >= 0 && projectedTotalLiters !== null && forecastMode === 'standalone') {
      // Option A: Standalone forecast bar at the end
      let forecastLabel = 'Today (Proj)';
      if (resolution === 'hourly') forecastLabel = 'Hour (Proj)';
      else if (resolution === 'weekly') forecastLabel = 'This Wk (Proj)';
      else if (resolution === 'monthly') forecastLabel = 'This Mo (Proj)';
      else if (resolution === 'yearly') forecastLabel = 'This Yr (Proj)';

      labels.push(forecastLabel);

      // Usage dataset (ends with null where the forecast bar sits)
      datasets.push({
        label: `Usage (${unit})`,
        data: [...values, null],
        backgroundColor: color + 'a0',
        hoverBackgroundColor: color,
        borderRadius: 6,
        borderWidth: 0,
        stack: 'consumption',
      });

      // Standalone Forecast dataset (null for all actual bars, positive at the appended index)
      const forecastValues = new Array(values.length).fill(null);
      const projectedVal = projectedTotalLiters * (isImperial ? 0.264172 : 1);
      forecastValues.push(projectedVal);

      datasets.push({
        label: `Forecast (${unit})`,
        data: forecastValues,
        backgroundColor: 'rgba(168, 85, 247, 0.40)',
        hoverBackgroundColor: 'rgba(168, 85, 247, 0.65)',
        borderColor: 'rgba(168, 85, 247, 0.9)',
        borderWidth: 2,
        borderDash: [4, 4],
        borderRadius: 6,
        stack: 'consumption',
      });
    } else {
      // Standard usage dataset
      datasets.push({
        label: `Usage (${unit})`,
        data: values,
        backgroundColor: color + 'a0',
        hoverBackgroundColor: color,
        borderRadius: 6,
        borderWidth: 0,
        stack: 'consumption',
      });

      // Option B: Stacked remainder on the current bucket
      if (showForecast && inProgressIndex >= 0 && projectedTotalLiters !== null && forecastMode === 'stacked') {
        const remainderValues = values.map((_, idx) => {
          if (idx === inProgressIndex) {
            const actualLiters = resolveLiters(data[idx]) ?? 0;
            const remainder = (projectedTotalLiters ?? 0) - actualLiters;
            return remainder > 0 ? remainder * (isImperial ? 0.264172 : 1) : null;
          }
          return null;
        });

        datasets.push({
          label: `Projected Remainder (${unit})`,
          data: remainderValues,
          backgroundColor: 'rgba(168, 85, 247, 0.45)',
          hoverBackgroundColor: 'rgba(168, 85, 247, 0.65)',
          borderColor: 'rgba(168, 85, 247, 0.85)',
          borderWidth: 1,
          borderDash: [3, 3],
          borderRadius: 6,
          stack: 'consumption',
        });
      }
    }

    // ML Behavioral Baseline Benchmark Curve (Hourly diurnal or Daily day-of-week)
    const isStandaloneAppended = showForecast && inProgressIndex >= 0 && projectedTotalLiters !== null && forecastMode === 'standalone';

    if (showBaseline && baseline) {
      if (resolution === 'hourly' && Array.isArray(baseline.hourly_avg_flow) && baseline.hourly_avg_flow.length >= 24) {
        const hourlyBaseline = data.map((d: any) => {
          if (!d?.bucket) return null;
          const dt = new Date(d.bucket);
          if (isNaN(dt.getTime())) return null;
          const h = dt.getHours();
          const l = baseline.hourly_avg_flow[h];
          return (typeof l === 'number' && Number.isFinite(l)) ? l * (isImperial ? 0.264172 : 1) : null;
        });
        if (isStandaloneAppended) {
          hourlyBaseline.push(null);
        }

        datasets.push({
          type: 'line',
          label: `ML Diurnal Baseline (${unit})`,
          data: hourlyBaseline,
          borderColor: 'rgba(245, 158, 11, 0.9)',
          backgroundColor: 'rgba(245, 158, 11, 0.08)',
          borderWidth: 2,
          borderDash: [5, 4],
          pointRadius: 2.5,
          pointHoverRadius: 5,
          pointBackgroundColor: '#f59e0b',
          tension: 0.3,
          fill: false,
          yAxisID: 'y',
          order: 1,
        });
      } else if (resolution === 'daily' && Array.isArray(baseline.daily_avg_consumption) && baseline.daily_avg_consumption.length >= 7) {
        const dailyBaseline = data.map((d: any) => {
          if (!d?.bucket) return null;
          const dt = new Date(d.bucket);
          if (isNaN(dt.getTime())) return null;
          // In python: 0=Mon, 1=Tue, ..., 6=Sun; In JS: 0=Sun, 1=Mon, ..., 6=Sat
          const dow = (dt.getDay() + 6) % 7;
          const l = baseline.daily_avg_consumption[dow];
          return (typeof l === 'number' && Number.isFinite(l)) ? l * (isImperial ? 0.264172 : 1) : null;
        });
        if (isStandaloneAppended) {
          dailyBaseline.push(null);
        }

        datasets.push({
          type: 'line',
          label: `ML Weekly Baseline (${unit})`,
          data: dailyBaseline,
          borderColor: 'rgba(245, 158, 11, 0.9)',
          backgroundColor: 'rgba(245, 158, 11, 0.08)',
          borderWidth: 2,
          borderDash: [5, 4],
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#f59e0b',
          tension: 0.2,
          fill: false,
          yAxisID: 'y',
          order: 1,
        });
      }
    }

    const hasBaselineCurve = showBaseline && !!baseline && (
      (resolution === 'hourly' && Array.isArray(baseline.hourly_avg_flow) && baseline.hourly_avg_flow.length >= 24) ||
      (resolution === 'daily' && Array.isArray(baseline.daily_avg_consumption) && baseline.daily_avg_consumption.length >= 7)
    );

    chart = new Chart(canvasEl, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: showForecast || hasBaselineCurve,
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 11, family: 'Inter', weight: 500 },
              color: 'hsl(0 0% 60%)'
            }
          },
          tooltip: {
            backgroundColor: 'hsl(0 0% 12% / 0.95)',
            titleFont: { size: 12, family: 'Inter', weight: 'bold' },
            bodyFont: { size: 12, family: 'Inter' },
            padding: 12,
            cornerRadius: 10,
            displayColors: false,
            callbacks: {
              title: (contexts) => {
                const idx = contexts[0]?.dataIndex;
                if (idx === data.length) {
                  // This is the appended standalone forecast bar
                  if (resolution === 'hourly') return 'Current Hour • Run-Rate Forecast';
                  if (resolution === 'weekly') return 'This Week • Run-Rate Forecast';
                  if (resolution === 'monthly') return 'This Month • Run-Rate Forecast';
                  if (resolution === 'yearly') return 'This Year • Run-Rate Forecast';
                  return "Today's Projected Total • Forecast";
                }
                const item = data[idx];
                if (!item?.bucket) return '';
                const dt = new Date(item.bucket);
                if (resolution === 'hourly') {
                  return dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                }
                if (resolution === 'weekly') {
                  return `Week of ${dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
                }
                if (resolution === 'monthly') {
                  return dt.toLocaleDateString([], { month: 'long', year: 'numeric' });
                }
                if (resolution === 'yearly') {
                  return dt.toLocaleDateString([], { year: 'numeric' });
                }
                return dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
              },
              label: (context: any) => {
                if (context?.parsed?.y == null) return '';
                const ds = context?.dataset ?? {};
                const isLine = ds.type === 'line';
                const isForecast = ds.label?.includes('Forecast') || ds.label?.includes('Projected');

                if (isLine) {
                  return `${ds.label}: ${context.parsed.y.toFixed(2)} ${unit} (ML Model)`;
                }

                const vol = `${context.parsed.y.toFixed(2)} ${unit}`;
                let prefix = 'Usage';
                if (isForecast) {
                  prefix = forecastMode === 'standalone' ? 'Projected Total' : 'Projected Remaining';
                }

                const lines: string[] = [];
                if (!showCost) {
                  lines.push(`${prefix}: ${vol}`);
                } else {
                  const cost = customUnit === 'kWh'
                    ? context.parsed.y * 0.22
                    : (customUnit === 'cu_ft' || customUnit === 'cf')
                      ? context.parsed.y * 0.01037 * 1.45
                      : (isImperial ? context.parsed.y * 0.008 : context.parsed.y * 0.00211);
                  const costLabel = customUnit === 'kWh' ? 'Est. Electricity Cost' : ((customUnit === 'cu_ft' || customUnit === 'cf') ? 'Est. Gas Cost' : 'Est. Municipal Cost');
                  lines.push(`${prefix}: ${vol}`);
                  lines.push(`${costLabel}: $${cost.toFixed(2)}`);
                }

                // If this is an actual usage bar and baseline is active, add comparative difference
                if (!isForecast && showBaseline && baseline && typeof context.dataIndex === 'number' && context.dataIndex < data.length) {
                  const item = data[context.dataIndex];
                  if (item?.bucket) {
                    const dt = new Date(item.bucket);
                    let baseLiters: number | null = null;
                    let stddevLiters: number | null = null;
                    if (resolution === 'hourly' && baseline.hourly_avg_flow) {
                      baseLiters = baseline.hourly_avg_flow[dt.getHours()] ?? null;
                      if (baseline.hourly_stddev_flow) {
                        stddevLiters = baseline.hourly_stddev_flow[dt.getHours()] ?? null;
                      }
                    } else if (resolution === 'daily' && baseline.daily_avg_consumption) {
                      baseLiters = baseline.daily_avg_consumption[(dt.getDay() + 6) % 7] ?? null;
                    }

                    if (typeof baseLiters === 'number' && baseLiters > 0) {
                      const baseVal = baseLiters * (isImperial ? 0.264172 : 1);
                      const pct = ((context.parsed.y - baseVal) / baseVal) * 100;
                      const sign = pct >= 0 ? '+' : '';
                      lines.push(`Typical ML Baseline: ${baseVal.toFixed(2)} ${unit} (${sign}${pct.toFixed(0)}%)`);

                      // Highlight if anomaly (+2 stddev)
                      if (typeof stddevLiters === 'number' && stddevLiters > 0) {
                        const threshold = (baseLiters + 2 * stddevLiters) * (isImperial ? 0.264172 : 1);
                        if (context.parsed.y > threshold) {
                          lines.push('⚠️ Elevated flow (> 2σ above diurnal baseline)');
                        }
                      }
                    }
                  }
                }

                if (isForecast && forecastMode === 'standalone') {
                  lines.push('⚡ Statistical run-rate pace');
                }

                return lines.length === 1 ? lines[0] : lines;
              }
            }
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 10, family: 'Inter', weight: 500 },
              color: 'hsl(0 0% 50%)',
              maxTicksLimit: resolution === 'monthly' ? 12 : resolution === 'yearly' ? 6 : (resolution === 'weekly' ? 8 : 12),
            },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'hsl(0 0% 50% / 0.1)',
              lineWidth: 0.5,
            },
            ticks: {
              font: { size: 10, family: 'Inter' },
              color: 'hsl(0 0% 50%)',
              maxTicksLimit: 5,
              callback: (val) => `${val} ${unit}`
            },
            border: { display: false },
          },
        },
      },
    });
  }

  $effect(() => {
    if (data && canvasEl) {
      $unitPreference; // register dependency
      buildChart();
    }
  });

  onDestroy(() => {
    if (chart) chart.destroy();
  });
</script>

<div class="w-full h-full min-h-[260px] relative group">
  <canvas bind:this={canvasEl} aria-label="Periodic water consumption chart"></canvas>
</div>
